import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, CheckCircle, Settings, Droplets, Filter, RotateCw, Mail, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';
import CleaningOptionsComparison from '../components/CleaningOptionsComparison';
import AlternativesBand from '../components/AlternativesBand';

const BIGSeriesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="BIG Inline Pressure Filters | Brush & Vacuum | CTC Waterworks"
        description="BIG family overview with brush and vacuum options, manual or automatic cleaning. Modular inline pressure filters for drip, micro-sprinkler, pivots, and intake polishing."
        canonical="https://www.ctcwaterworks.com/filtration/inline/big"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline" className="text-gray-600 hover:text-ctc-blue">Inline</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">BIG Series</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content - Left 2/3 */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-5xl font-bold text-gray-900 mb-4">
                  BIG Series (Blue Inline Gasketed)
                </h1>
                <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                  Precision, gasket-sealed in-line filters for leak-free, consistent filtration. Designed for any pressurized water system — agricultural, municipal, industrial, or hydropower — delivering durable stainless-steel performance with manual or automated cleaning.
                </p>

                {/* Product Image */}
                <div className="mb-6 rounded-lg overflow-hidden shadow-xl">
                  <img
                    src="/BIG Catalog Pic.jpeg"
                    alt="BIG Series inline pressure filter - stainless steel construction with blue gasket seal"
                    className="w-full h-auto"
                  />
                </div>

                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="font-bold text-lg mb-3 text-gray-900">BIG = Blue Inline Gasketed</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The BIG Series is CTC Waterworks' flagship inline pressure filter platform. Versatile inline pressure filter supporting multiple cleaning technologies across all industries. Protect pumps, valves, heat exchangers, and emitters with reliable stainless-steel construction. Choose your filter body size, select your screen type and mesh, then add the cleaning technology that fits your water quality and maintenance preferences.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Rail - Downloads & CTAs */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-xl p-6 space-y-4 sticky top-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Resources & Support</h3>

                <Link
                  to="/contact?topic=filtration"
                  className="block w-full bg-ctc-blue text-white px-6 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center shadow-lg"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Specialist
                </Link>

                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">Download Resources</h4>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>BIG Series Sell Sheet (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Dimensional Drawing (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Spec Table (CSV)</span>
                  </a>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3 text-gray-700 mb-3">
                    <Phone size={20} />
                    <span className="text-sm">(509) 901-7476</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail size={20} />
                    <a href="mailto:Andre@ctcwaterworks.com" className="text-sm hover:text-ctc-blue underline">
                      Andre@ctcwaterworks.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cleaning Options Info Strip */}
      <section className="py-6 bg-gradient-to-r from-blue-50 to-green-50 border-y border-blue-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Three Cleaning Approaches Available</h3>
              <p className="text-gray-700 text-sm">
                Supports Manual, Semi-Automatic (BIF & MVC), and Fully Automatic (RAB & AVC) cleaning options.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/filtration/inline/cleaning/manual"
                className="text-xs bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Manual
              </Link>
              <Link
                to="/filtration/inline/cleaning/semi-auto"
                className="text-xs bg-white border border-ctc-blue text-ctc-blue px-4 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Semi-Auto
              </Link>
              <Link
                to="/filtration/inline/cleaning/automatic"
                className="text-xs bg-white border border-green-600 text-green-600 px-4 py-2 rounded-lg font-semibold hover:bg-green-50 transition-colors"
              >
                Fully Auto
              </Link>
              <Link
                to="/filtration/inline/cleaning"
                className="text-xs bg-ctc-blue text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Compare All
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Why Choose BIG Series?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-6">
              <Settings className="text-ctc-blue mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Continuous Service</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                On-line cleaning options mean no system shutdowns. Flow continues while cleaning happens automatically or with simple hand-crank operation.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-lg p-6">
              <Droplets className="text-green-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Broad Size & Flow Range</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Available in 2" through 8" connection sizes and larger custom configurations. Handles flows from small drip zones to high-capacity mainlines.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
              <Filter className="text-orange-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Serviceable Components</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Standard mesh screens and perforated plates with quick-access design. Replace or change screen types in the field without special tools.
              </p>
            </div>

            <div className="bg-gradient-to-br from-ctc-blue/10 to-white border border-ctc-blue/30 rounded-lg p-6">
              <CheckCircle className="text-ctc-blue mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Highly Configurable</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Adaptable for drip irrigation, micro-sprinklers, center pivots, intake polishing, and industrial applications. One platform, multiple uses.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cleaning Technology Sub-Nav Cards */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Choose Your Cleaning Technology</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            BIG Series bodies support both Brush and Vacuum cleaning systems. Start with manual operation and retrofit to automatic later, or specify automatic from day one.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Brush Cleaning Card */}
            <div className="bg-white border-2 border-green-300 rounded-lg shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
              <div className="bg-gradient-to-br from-green-600 to-green-700 text-white p-6">
                <div className="flex items-center mb-3">
                  <RotateCw className="mr-3" size={40} />
                  <div>
                    <h3 className="text-2xl font-bold">Brush Cleaning</h3>
                    <p className="text-green-100 text-sm">Mechanical Scrubbing Action</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Rotating brush assembly scrubs the screen surface to remove fibrous debris, organic matter, and algae. Available in manual hand-crank or fully automatic sensor-triggered operation.
                </p>

                <div className="bg-green-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Available Options:</h4>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                      <div>
                        <span className="font-semibold text-gray-900">BIF (Manual Brush):</span>
                        <span className="text-gray-700 text-sm"> Hand-crank initiated cleaning cycles</span>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                      <div>
                        <span className="font-semibold text-gray-900">RAB (Automatic Brush):</span>
                        <span className="text-gray-700 text-sm"> Sensor or timer-triggered automatic cleaning</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Link
                  to="/filtration/inline/big/brush-cleaning"
                  className="flex items-center justify-between bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  <span>Explore Brush Cleaning</span>
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>

            {/* Vacuum Cleaning Card */}
            <div className="bg-white border-2 border-orange-300 rounded-lg shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
              <div className="bg-gradient-to-br from-orange-600 to-orange-700 text-white p-6">
                <div className="flex items-center mb-3">
                  <Droplets className="mr-3" size={40} />
                  <div>
                    <h3 className="text-2xl font-bold">Vacuum Cleaning</h3>
                    <p className="text-orange-100 text-sm">High-Suction Backflush</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Vacuum scanner creates powerful suction to extract heavy debris through rapid backflush cycles. Ideal for sand, silt, and high solids loading with minimal water loss.
                </p>

                <div className="bg-orange-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Available Options:</h4>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                      <div>
                        <span className="font-semibold text-gray-900">MVC (Manual Vacuum):</span>
                        <span className="text-gray-700 text-sm"> Hand-crank initiated vacuum cycles</span>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                      <div>
                        <span className="font-semibold text-gray-900">AVC (Automatic Vacuum):</span>
                        <span className="text-gray-700 text-sm"> Automatic sensor-driven cleaning cycles</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Link
                  to="/filtration/inline/big/vacuum-cleaning"
                  className="flex items-center justify-between bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                >
                  <span>Explore Vacuum Cleaning</span>
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </div>

          <CleaningOptionsComparison />

          <div className="mt-8 bg-blue-50 border-l-4 border-ctc-blue rounded-r-lg p-6 max-w-4xl mx-auto">
            <h4 className="font-bold text-gray-900 mb-2">Cleaning Assembly Availability</h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              Cleaning assemblies (BIF/MVC/RAB/AVC) availability varies by model and size. See <Link to="/filtration/parts" className="text-ctc-blue hover:underline font-semibold">Parts & Accessories</Link> for complete compatibility information.
            </p>
          </div>
        </div>
      </section>

      {/* Applications Strip */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Common Applications Across Industries</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            BIG Series filters serve diverse water systems across agriculture, municipal, industrial, and hydropower sectors
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
              <Droplets className="mx-auto mb-3 text-green-600" size={36} />
              <h3 className="font-semibold text-gray-900 text-sm mb-2">Irrigation Systems</h3>
              <p className="text-xs text-gray-600 leading-relaxed">Drip, micro, pivot, and solid-set systems for orchards, vineyards, and row crops</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
              <Settings className="mx-auto mb-3 text-blue-600" size={36} />
              <h3 className="font-semibold text-gray-900 text-sm mb-2">Municipal Water & Wastewater</h3>
              <p className="text-xs text-gray-600 leading-relaxed">Raw-water intake, pre-filtration, and pump-station protection for treatment facilities</p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
              <Filter className="mx-auto mb-3 text-orange-600" size={36} />
              <h3 className="font-semibold text-gray-900 text-sm mb-2">Industrial Process Water</h3>
              <p className="text-xs text-gray-600 leading-relaxed">Food & beverage, packing-line wash water, coolant filtration, and rinse-water recovery</p>
            </div>

            <div className="bg-gradient-to-br from-ctc-blue/10 to-white border border-ctc-blue/30 rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
              <CheckCircle className="mx-auto mb-3 text-ctc-blue" size={36} />
              <h3 className="font-semibold text-gray-900 text-sm mb-2">Hydropower & Dams</h3>
              <p className="text-xs text-gray-600 leading-relaxed">Intake protection with fish-safe screen configurations meeting NOAA/WDFW criteria</p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-300 rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
              <RotateCw className="mx-auto mb-3 text-gray-700" size={36} />
              <h3 className="font-semibold text-gray-900 text-sm mb-2">Infrastructure & Construction</h3>
              <p className="text-xs text-gray-600 leading-relaxed">Reuse water filtration, dewatering, and sediment control for large-scale works</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
              <Droplets className="mx-auto mb-3 text-blue-600" size={36} />
              <h3 className="font-semibold text-gray-900 text-sm mb-2">Intake & Canal Filtration</h3>
              <p className="text-xs text-gray-600 leading-relaxed">Surface-water intake and canal protection for pumps, membranes, and mechanical systems</p>
            </div>
          </div>

          <div className="mt-8 bg-gradient-to-r from-blue-50 to-gray-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-5xl mx-auto">
            <h4 className="font-bold text-gray-900 mb-2">Engineered for Any Water Source</h4>
            <p className="text-gray-700 leading-relaxed">
              BIG Series filters perform reliably in agricultural, municipal, industrial, and hydropower applications. From irrigation networks to process-water loops and dam intakes, these stainless-steel filters provide durable, low-maintenance solids removal wherever clean water matters.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Bank Configuration Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Scalable Bank Configurations</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                BIG Series filters can be installed as individual units or arranged in parallel filter banks for high-flow applications. Multiple filters provide redundancy, allow staged cleaning without flow interruption, and scale capacity to match system demand.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="mr-3 text-ctc-blue mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Parallel Operation</h4>
                    <p className="text-sm text-gray-700">Multiple filters distribute flow and provide backup capacity for continuous operation</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="mr-3 text-green-600 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Staged Cleaning</h4>
                    <p className="text-sm text-gray-700">Clean one filter while others remain online—no system shutdowns required</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="mr-3 text-orange-600 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Future Expansion</h4>
                    <p className="text-sm text-gray-700">Add filters to existing banks as water demand increases over time</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden shadow-2xl">
              <img
                src="/BIG Bank.jpeg"
                alt="BIG Series filter bank installation showing multiple inline pressure filters configured in parallel for high-capacity filtration"
                className="w-full h-auto"
              />
              <div className="bg-gradient-to-r from-ctc-blue to-blue-700 text-white px-6 py-3">
                <p className="text-sm font-semibold">Filter Bank Configuration</p>
                <p className="text-xs text-blue-100">Multiple BIG Series filters arranged for high-flow applications</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Construction & Configuration */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Construction & Configuration</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Body Options</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="mr-3 text-ctc-blue mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Connection sizes: 2", 3", 4", 6", 8" and custom larger sizes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-3 text-ctc-blue mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Material: 304 stainless steel or epoxy-coated carbon steel</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-3 text-ctc-blue mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Pressure rating: 150 PSI working pressure (higher ratings available)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-3 text-ctc-blue mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Gasketed top head for quick screen access</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Screen Options</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="mr-3 text-green-600 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Mesh screens: 20 to 120 mesh (standard), custom sizes available</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-3 text-green-600 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Perforated plate: 1/32", 1/16", 1/8" holes and custom patterns</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-3 text-green-600 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Wedgewire screens for specialized applications</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-3 text-green-600 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Field-replaceable without special tools or repiping</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">BIG Series (Blue Inline Gasketed) — Specifications</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Core BIG platform specifications. Choose your cleaning technology (brush or vacuum) separately.
          </p>

          {/* Dimension Diagram */}
          <div className="mb-8 bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Dimensional Reference</h3>
            <div className="flex justify-center">
              <img
                src="/BIG Dimension Diagram.png"
                alt="BIG Series Dimensional Diagram showing measurements A, B, C, D, E, and F"
                className="max-w-full h-auto"
              />
            </div>
          </div>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-ctc-blue to-blue-700 text-white sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Item #</th>
                  <th className="px-4 py-3 text-left font-semibold">Connection</th>
                  <th className="px-4 py-3 text-left font-semibold">Size Label</th>
                  <th className="px-4 py-3 text-left font-semibold">Flow (GPM)</th>
                  <th className="px-4 py-3 text-left font-semibold">A (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">B – Screen Length (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">C (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">D (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">E (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">F (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">Approx. Weight (lb)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">CA63</td>
                  <td className="px-4 py-3">3 NPTM</td>
                  <td className="px-4 py-3">3"</td>
                  <td className="px-4 py-3">150–300</td>
                  <td className="px-4 py-3">23⅛</td>
                  <td className="px-4 py-3">18</td>
                  <td className="px-4 py-3">1½</td>
                  <td className="px-4 py-3">7</td>
                  <td className="px-4 py-3">34</td>
                  <td className="px-4 py-3">—</td>
                  <td className="px-4 py-3">44</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">CA183</td>
                  <td className="px-4 py-3">3 FLG</td>
                  <td className="px-4 py-3">3"</td>
                  <td className="px-4 py-3">150–300</td>
                  <td className="px-4 py-3">23½</td>
                  <td className="px-4 py-3">18</td>
                  <td className="px-4 py-3">1½</td>
                  <td className="px-4 py-3">7</td>
                  <td className="px-4 py-3">34</td>
                  <td className="px-4 py-3">6</td>
                  <td className="px-4 py-3">65</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">CA64</td>
                  <td className="px-4 py-3">4 NPTM</td>
                  <td className="px-4 py-3">4"</td>
                  <td className="px-4 py-3">300–450</td>
                  <td className="px-4 py-3">29¼</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">2½</td>
                  <td className="px-4 py-3">8</td>
                  <td className="px-4 py-3">41</td>
                  <td className="px-4 py-3">—</td>
                  <td className="px-4 py-3">50</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">CA184</td>
                  <td className="px-4 py-3">4 FLG</td>
                  <td className="px-4 py-3">4"</td>
                  <td className="px-4 py-3">300–450</td>
                  <td className="px-4 py-3">28¾</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">2½</td>
                  <td className="px-4 py-3">8</td>
                  <td className="px-4 py-3">41</td>
                  <td className="px-4 py-3">6½</td>
                  <td className="px-4 py-3">75</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">DA149</td>
                  <td className="px-4 py-3">6 FLG</td>
                  <td className="px-4 py-3">6"</td>
                  <td className="px-4 py-3">450–800</td>
                  <td className="px-4 py-3">45⅜</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">10</td>
                  <td className="px-4 py-3">60</td>
                  <td className="px-4 py-3">8¾</td>
                  <td className="px-4 py-3">97</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">DA157</td>
                  <td className="px-4 py-3">8 FLG</td>
                  <td className="px-4 py-3">8"</td>
                  <td className="px-4 py-3">800–1000</td>
                  <td className="px-4 py-3">56⅜</td>
                  <td className="px-4 py-3">48</td>
                  <td className="px-4 py-3">3</td>
                  <td className="px-4 py-3">12</td>
                  <td className="px-4 py-3">71¼</td>
                  <td className="px-4 py-3">10</td>
                  <td className="px-4 py-3">160</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">DA158</td>
                  <td className="px-4 py-3">10 FLG S</td>
                  <td className="px-4 py-3">10" S</td>
                  <td className="px-4 py-3">1000–1500</td>
                  <td className="px-4 py-3">69¾</td>
                  <td className="px-4 py-3">60</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">84</td>
                  <td className="px-4 py-3">11</td>
                  <td className="px-4 py-3">200</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">DA180</td>
                  <td className="px-4 py-3">10 FLG M</td>
                  <td className="px-4 py-3">10" M</td>
                  <td className="px-4 py-3">1500–2500</td>
                  <td className="px-4 py-3">99¾</td>
                  <td className="px-4 py-3">90</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">114</td>
                  <td className="px-4 py-3">11</td>
                  <td className="px-4 py-3">238</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">DA181</td>
                  <td className="px-4 py-3">10 FLG L</td>
                  <td className="px-4 py-3">10" L</td>
                  <td className="px-4 py-3">2500–3000</td>
                  <td className="px-4 py-3">129¾</td>
                  <td className="px-4 py-3">120</td>
                  <td className="px-4 py-3">4</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3">144</td>
                  <td className="px-4 py-3">11</td>
                  <td className="px-4 py-3">268</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">BA655</td>
                  <td className="px-4 py-3">12 FLG</td>
                  <td className="px-4 py-3">12"</td>
                  <td className="px-4 py-3">3200–5000</td>
                  <td className="px-4 py-3">164</td>
                  <td className="px-4 py-3">168</td>
                  <td className="px-4 py-3">6 FLG</td>
                  <td className="px-4 py-3">16</td>
                  <td className="px-4 py-3">184</td>
                  <td className="px-4 py-3">12</td>
                  <td className="px-4 py-3">320</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed mb-2">
              <span className="font-semibold text-gray-900">B = Screen Length.</span>
            </p>
            <p className="text-gray-700 leading-relaxed">
              Dimensions are for body only—cleaning mechanisms (brush or vacuum) add height. Custom mesh sizes from 20-120 mesh and perforation patterns available. Contact engineering for detailed dimensional drawings and cleaning technology selection guidance.
            </p>
          </div>
        </div>
      </section>

      {/* Mesh/Perf Reference */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mesh & Perforation Reference</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Standard mesh sizes and perforation equivalents for BIG Series filters.
          </p>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-ctc-blue to-blue-700 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Mesh</th>
                  <th className="px-6 py-4 text-left font-semibold">Open Area (%)</th>
                  <th className="px-6 py-4 text-left font-semibold">Equivalent Perf</th>
                  <th className="px-6 py-4 text-left font-semibold">Typical Use</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-blue-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">30</td>
                  <td className="px-6 py-4 text-gray-700">50</td>
                  <td className="px-6 py-4 text-gray-700">0.020–0.025"</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Intake protection</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">60</td>
                  <td className="px-6 py-4 text-gray-700">31</td>
                  <td className="px-6 py-4 text-gray-700">0.015–0.020"</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Micro-sprinkler</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">80</td>
                  <td className="px-6 py-4 text-gray-700">31</td>
                  <td className="px-6 py-4 text-gray-700">0.010–0.012"</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Drip irrigation</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">120</td>
                  <td className="px-6 py-4 text-gray-700">31</td>
                  <td className="px-6 py-4 text-gray-700">0.008–0.010"</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Fine emitter protection</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Custom Options:</span> Not limited to standard mesh sizes. Custom perforations and wedgewire screens available for specialized applications. Contact engineering for recommendations based on your water quality and emitter requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-Links Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Related Products & Technologies</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/filtration/inline/tig"
              className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">TIG Series</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                True Inline Gasketed filters for compact layouts and space-constrained installations. Manual cleaning only.
              </p>
              <div className="flex items-center text-ctc-blue font-semibold">
                Compare TIG Series
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/self-cleaning"
              className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Self-Cleaning Tech</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Detailed comparison of RAB automatic brush and AVC automatic vacuum cleaning technologies.
              </p>
              <div className="flex items-center text-ctc-blue font-semibold">
                Compare Technologies
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/suction-screens"
              className="bg-gradient-to-br from-green-50 to-white border-2 border-green-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Intake Protection</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Self-cleaning suction screens (SCS2) and passive fish-friendly screens (PSF) for upstream debris control.
              </p>
              <div className="flex items-center text-ctc-blue font-semibold">
                View Suction Screens
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Alternatives Band */}
      <AlternativesBand
        alternatives={[
          {
            title: "Need stainless value?",
            description: "BIG-S offers precision filtration in a cost-optimized stainless body with modular ports for low-cost installs.",
            link: "/filtration/inline/big-s"
          },
          {
            title: "On a tighter budget?",
            description: "GNG provides budget-friendly in-line filtration with a ring-lock quick-release head for easy maintenance.",
            link: "/filtration/inline/gng"
          }
        ]}
      />

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-ctc-blue to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Every Drop — in Any Application</h2>
          <p className="text-xl text-blue-100 mb-6">
            Whether you're safeguarding irrigation lines, pre-treating municipal water, filtering industrial process streams, or protecting hydropower intakes, CTC BIG Series Filters deliver reliable, stainless-steel performance you can trust.
          </p>
          <p className="text-lg text-blue-200 mb-8">
            Talk to a Filtration Specialist today to get a system tailored to your flow rates, solids profile, and operational needs.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Talk to a Filtration Specialist
            </Link>

            <Link
              to="/filtration/size-my-filter"
              className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Settings className="mr-2" size={20} />
              Size My Filter
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-blue-100">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BIGSeriesPage;
